import { useEffect, useState } from "react";
import { useSeo } from "@/hooks/useSeo";
import { Reveal, Eyebrow, PageHero } from "@/components/Reveal";

export default function About() {
  useSeo(
    "About | Reform Intelligence®",
    "Reform Intelligence AI, Inc. is an independent, Delaware public benefit corporation building the evaluation and governance infrastructure for trustworthy AI."
  );

  const [founder, setFounder] = useState(null);

  useEffect(() => {
    fetch("/content/founder.json")
      .then((r) => r.json())
      .then(setFounder)
      .catch(() => setFounder(null));
  }, []);

  return (
    <>
      <PageHero
        eyebrow="About"
        headline="An institution built to last."
        subhead="Reform Intelligence AI, Inc. is an independent evaluation and governance company, structured as a Delaware public benefit corporation."
      />

      <section className="bg-white">
        <div className="mx-auto max-w-6xl px-5 py-20 sm:px-8 sm:py-24">
          <Reveal>
            <Eyebrow>Mission</Eyebrow>
            <h2 className="mt-4 max-w-2xl font-serif2 text-3xl font-semibold tracking-tight text-[#1F2D4E]">We exist to make artificial intelligence accountable.</h2>
            <p className="mt-6 max-w-3xl text-lg leading-relaxed text-[#1A1A1A]">
              As AI is wired into the systems that run society, the world needs an independent institution that can measure whether those systems are reliable, safe, and worthy of trust. We are building that institution, and we intend for it to become the reference the market relies on.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="border-y border-[#E4E7EC] bg-[#F5F6F8]">
        <div className="mx-auto max-w-6xl px-5 py-20 sm:px-8 sm:py-24">
          <Reveal>
            <Eyebrow>Structure</Eyebrow>
            <h2 className="mt-4 max-w-2xl font-serif2 text-3xl font-semibold tracking-tight text-[#1F2D4E]">A public benefit corporation.</h2>
            <p className="mt-6 max-w-3xl text-lg leading-relaxed text-[#1A1A1A]">
              Reform Intelligence AI, Inc. is incorporated in Delaware as a public benefit corporation. Our chartered purpose commits us to the public interest in trustworthy AI, alongside our responsibilities to shareholders. We operate with the discipline and disclosure of a public institution from day one.
            </p>
          </Reveal>
        </div>
      </section>

      <section data-testid="founder-section" className="bg-white">
        <div className="mx-auto max-w-6xl px-5 py-20 sm:px-8 sm:py-24">
          <Reveal>
            <Eyebrow>Founder</Eyebrow>
          </Reveal>
          {founder && (
            <div className="mt-10 grid grid-cols-1 gap-12 lg:grid-cols-[1fr_1.6fr] lg:gap-16">
              <Reveal>
                <div className="border border-[#E4E7EC] bg-[#F5F6F8] p-3">
                  <img
                    src={founder.headshot}
                    alt={`${founder.name}, ${founder.title} of Reform Intelligence`}
                    data-testid="founder-headshot"
                    className="w-full object-cover"
                  />
                </div>
              </Reveal>
              <Reveal delay={0.1}>
                <h2 className="font-serif2 text-3xl font-semibold text-[#1F2D4E]">{founder.name}</h2>
                <p className="mt-2 text-sm font-semibold uppercase tracking-[0.15em] text-[#B08D3F]">
                  {founder.title}{founder.credentials ? ` · ${founder.credentials}` : ""}
                </p>
                <div className="mt-6 space-y-5 leading-relaxed text-[#1A1A1A]">
                  {founder.bio.map((p, i) => (
                    <p key={i}>{p}</p>
                  ))}
                </div>
              </Reveal>
            </div>
          )}
        </div>
      </section>

      <section className="border-t border-[#E4E7EC] bg-[#F5F6F8]">
        <div className="mx-auto max-w-6xl px-5 py-20 sm:px-8 sm:py-24">
          <Reveal>
            <Eyebrow>Governance</Eyebrow>
            <h2 className="mt-4 max-w-2xl font-serif2 text-3xl font-semibold tracking-tight text-[#1F2D4E]">We hold ourselves to the standards we ask of others.</h2>
            <p className="mt-6 max-w-3xl text-lg leading-relaxed text-[#1A1A1A]">
              The credibility of an evaluator rests on how it governs itself. We hold ourselves to the standards we ask of others: independence, transparency, and accountability. As our Board of Directors and Advisory Board are seated, they will be listed here.
            </p>
          </Reveal>
        </div>
      </section>
    </>
  );
}
